/**
 * Load mapbox-gl when a map is actually shown. P25009-378 BR-2.
 *
 * mapbox-gl is 668 KB of JavaScript (141 KB over the wire) and it used to sit
 * in the <head> of every page, put there by util/includeScripts.js. The search
 * results list, the landing page and every listing page paid for it without
 * drawing a single map: on the live search page the library was fetched with
 * zero map canvases on the page.
 *
 * Three things in the app touch Mapbox, and only the first genuinely needs this
 * library:
 *
 *   the draggable map      DynamicMapboxMap / SearchMapWithMapbox — needs it
 *   the small static map   an <img> from Mapbox's static API — only needs a token
 *   location autocomplete  the separate mapbox-sdk.min.js (12 KB), which stays
 *                          in the <head> because the Topbar search uses it
 *
 * The access token lives here too. It comes from the marketplace config, and
 * both this loader and the geocoder need it, so it is published once by
 * IncludeScripts rather than read off window.mapboxgl (which no longer exists
 * until a map is drawn).
 */

import { useEffect, useState } from 'react';

const MAPBOX_GL_VERSION = 'v1.0.0';
const MAPBOX_GL_JS = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_GL_VERSION}/mapbox-gl.js`;
const MAPBOX_GL_CSS = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_GL_VERSION}/mapbox-gl.css`;

let accessToken = null;
let loadPromise = null;

/**
 * Remember the marketplace's Mapbox token, and hand it to mapbox-gl if that is
 * already loaded. Called by IncludeScripts, which has the config.
 *
 * @param {String} token
 */
export const setMapboxAccessToken = token => {
  accessToken = token || null;
  if (typeof window !== 'undefined' && window.mapboxgl && accessToken) {
    window.mapboxgl.accessToken = accessToken;
  }
};

/**
 * The token, preferring the one published from config and falling back to
 * whatever mapbox-gl holds (it sets its own once configured).
 *
 * @return {String|null}
 */
export const getMapboxAccessToken = () =>
  accessToken || (typeof window !== 'undefined' ? window.mapboxgl?.accessToken || null : null);

/** Whether mapbox-gl is on the page right now. */
export const isMapboxGlLoaded = () => typeof window !== 'undefined' && !!window.mapboxgl;

/**
 * Add mapbox-gl (and its stylesheet) to the page, at most once however many
 * maps ask for it. Resolves true when the library is usable.
 *
 * Never rejects: a map that cannot load its library renders as an empty box,
 * which is how the app already behaved before the library was in the <head>.
 *
 * @return {Promise<Boolean>}
 */
export const ensureMapboxGl = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.mapboxgl) {
    setMapboxAccessToken(getMapboxAccessToken());
    return Promise.resolve(true);
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise(resolve => {
    if (!document.querySelector(`link[data-mapbox-gl-css]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = MAPBOX_GL_CSS;
      link.crossOrigin = 'anonymous';
      link.setAttribute('data-mapbox-gl-css', '');
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = MAPBOX_GL_JS;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener(
      'load',
      () => {
        setMapboxAccessToken(getMapboxAccessToken());
        resolve(true);
      },
      { once: true }
    );
    script.addEventListener(
      'error',
      () => {
        // Allow a later map to try again.
        loadPromise = null;
        resolve(false);
      },
      { once: true }
    );
    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Hook form: returns whether mapbox-gl is ready, and starts loading it the
 * first time a component says it needs it.
 *
 * @param {Boolean} enabled false for a component that does not need the library
 *                          (a static map, or a Google Maps marketplace)
 * @return {Boolean}
 */
export const useMapboxGl = (enabled = true) => {
  const [loaded, setLoaded] = useState(isMapboxGlLoaded);

  useEffect(() => {
    if (!enabled || loaded) {
      return undefined;
    }
    let cancelled = false;
    ensureMapboxGl().then(ok => {
      if (!cancelled && ok) {
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, loaded]);

  return loaded;
};
